import React, { useRef } from 'react';
import { FormFieldDef, FormSection } from '../../types/formSchema';

interface Props {
  sections: FormSection[];
  selectedFieldKey: string | null;
  onSelectField: (key: string) => void;
  onSectionsChange: (sections: FormSection[]) => void;
}

export default function FieldListEditor({ sections, selectedFieldKey, onSelectField, onSectionsChange }: Props) {
  const dragItem = useRef<{ sectionIdx: number; fieldIdx: number } | null>(null);
  const dragOver = useRef<{ sectionIdx: number; fieldIdx: number } | null>(null);

  const handleDragStart = (sectionIdx: number, fieldIdx: number) => {
    dragItem.current = { sectionIdx, fieldIdx };
  };

  const handleDragOver = (e: React.DragEvent, sectionIdx: number, fieldIdx: number) => {
    e.preventDefault();
    dragOver.current = { sectionIdx, fieldIdx };
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragItem.current || !dragOver.current) return;
    const from = dragItem.current;
    const to = dragOver.current;
    if (from.sectionIdx === to.sectionIdx && from.fieldIdx === to.fieldIdx) return;

    const next = sections.map((s) => ({ ...s, fields: [...s.fields] }));
    const [moved] = next[from.sectionIdx].fields.splice(from.fieldIdx, 1);
    next[to.sectionIdx].fields.splice(to.fieldIdx, 0, moved);

    onSectionsChange(next);
    dragItem.current = null;
    dragOver.current = null;
  };

  const addField = (sectionIdx: number) => {
    const next = sections.map((s) => ({ ...s, fields: [...s.fields] }));
    const newKey = `custom_${Date.now()}`;
    const newField: FormFieldDef = {
      key: newKey,
      type: 'text',
      label: 'New Field',
      required: false,
      locked: false,
    };
    next[sectionIdx].fields.push(newField);
    onSectionsChange(next);
    onSelectField(newKey);
  };

  const deleteField = (sectionIdx: number, fieldIdx: number) => {
    const field = sections[sectionIdx].fields[fieldIdx];
    if (field.locked) return;
    const next = sections.map((s) => ({ ...s, fields: [...s.fields] }));
    next[sectionIdx].fields.splice(fieldIdx, 1);
    onSectionsChange(next);
  };

  const addSection = () => {
    const newSection: FormSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      collapsible: true,
      fields: [],
    };
    onSectionsChange([...sections, newSection]);
  };

  const deleteSection = (sectionIdx: number) => {
    const section = sections[sectionIdx];
    if (section.fields.some((f) => f.locked)) return;
    const next = [...sections];
    next.splice(sectionIdx, 1);
    onSectionsChange(next);
  };

  const renameSection = (sectionIdx: number, title: string) => {
    const next = sections.map((s, i) => (i === sectionIdx ? { ...s, title } : s));
    onSectionsChange(next);
  };

  return (
    <div style={containerStyle}>
      <div style={toolbarStyle}>
        <button onClick={addSection} style={toolbarBtnStyle}>+ Section</button>
      </div>
      {sections.map((section, si) => (
        <div key={section.id} style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <input
              value={section.title}
              onChange={(e) => renameSection(si, e.target.value)}
              style={sectionTitleInputStyle}
            />
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => addField(si)} style={smallBtnStyle} title="Add field">+</button>
              {!section.fields.some((f) => f.locked) && (
                <button onClick={() => deleteSection(si)} style={{ ...smallBtnStyle, color: '#ef4444' }} title="Delete section">&times;</button>
              )}
            </div>
          </div>
          {section.fields.map((field, fi) => (
            <div
              key={field.key}
              draggable
              onDragStart={() => handleDragStart(si, fi)}
              onDragOver={(e) => handleDragOver(e, si, fi)}
              onDrop={handleDrop}
              onClick={() => onSelectField(field.key)}
              style={{
                ...fieldRowStyle,
                background: selectedFieldKey === field.key ? '#eff6ff' : '#fff',
                borderColor: selectedFieldKey === field.key ? '#3b82f6' : '#e5e7eb',
              }}
            >
              <span style={dragHandleStyle} title="Drag to reorder">⠿</span>
              {field.locked && <span style={lockIconStyle} title="Core field (cannot delete)">🔒</span>}
              <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {field.label}
              </span>
              <span style={{ fontSize: 10, color: '#9ca3af', marginRight: 4 }}>{field.type}</span>
              {field.required && <span style={requiredBadge}>req</span>}
              {!field.locked && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteField(si, fi); }}
                  style={deleteBtnStyle}
                  title="Delete field"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {section.fields.length === 0 && (
            <div style={{ padding: '12px 16px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
              No fields — click + to add one
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  background: '#f9fafb',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid #e5e7eb',
  background: '#fff',
};

const toolbarBtnStyle: React.CSSProperties = {
  fontSize: 12,
  padding: '4px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  color: '#374151',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 2,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb',
  borderTop: '1px solid #e5e7eb',
};

const sectionTitleInputStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  border: 'none',
  background: 'transparent',
  outline: 'none',
  flex: 1,
};

const smallBtnStyle: React.CSSProperties = {
  fontSize: 14,
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  color: '#374151',
};

const fieldRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid #e5e7eb',
  border: '1px solid #e5e7eb',
  borderTop: 'none',
  transition: 'background 0.1s',
};

const dragHandleStyle: React.CSSProperties = {
  cursor: 'grab',
  color: '#9ca3af',
  fontSize: 14,
  userSelect: 'none',
};

const lockIconStyle: React.CSSProperties = {
  fontSize: 12,
};

const requiredBadge: React.CSSProperties = {
  fontSize: 9,
  padding: '1px 4px',
  background: '#fef3c7',
  color: '#92400e',
  borderRadius: 3,
  fontWeight: 600,
};

const deleteBtnStyle: React.CSSProperties = {
  fontSize: 16,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#d1d5db',
  padding: '0 2px',
  lineHeight: 1,
};
